import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ArrowLeftIcon, CurrencyDollarIcon, CalendarIcon, UserIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { feeAPI, studentAPI, batchAPI, enrollmentAPI } from '../../services/api';
import { formatDateToIST, isDateInPast, formatIndianCurrency } from '../../utils/dateUtils';
import { useMutation } from '@tanstack/react-query';

const FeeDetail = () => {
  const { feeStructureId } = useParams();
  const navigate = useNavigate();

  const { data: feeStructure, isLoading } = useQuery({
    queryKey: ['feeStructure', feeStructureId],
    queryFn: () => feeAPI.getById(feeStructureId).then(res => res.data),
    enabled: !!feeStructureId,
  });

  const { data: students } = useQuery({
    queryKey: ['students'],
    queryFn: () => studentAPI.getAll(0, 100, true).then(res => res.data),
  });

  const { data: batches } = useQuery({
    queryKey: ['batches'],
    queryFn: () => batchAPI.getAll(0, 100, true).then(res => res.data),
  });

  const { data: enrollments } = useQuery({
    queryKey: ['enrollments'],
    queryFn: () => enrollmentAPI.getAll(0, 100, true).then(res => res.data),
  });

  const getStudentName = (studentId) => {
    const student = students?.find(s => s._id === studentId || s.id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : 'Unknown Student';
  };

  const getBatchName = (batchId) => {
    const batch = batches?.find(b => b._id === batchId || b.id === batchId);
    return batch ? `${batch.batch_name} - ${batch.subject}` : 'Batch Deleted';
  };

  const getSelectedBatch = () => {
    if (!feeStructure) return null;
    const batchId = feeStructure.batch?.id || feeStructure.batch_id;
    const selectedBatch = batches?.find(b => (b._id || b.id) === batchId);
    console.log('getSelectedBatch:', { batchId, selectedBatch, batches });
    return selectedBatch;
  };

  const calculatePaymentStatus = () => {
    if (!feeStructure) return { status: 'Unknown', color: 'bg-gray-900/30 text-gray-300 border border-gray-700' };
    
    // Get enrollment fee payment for this student-batch combination
    const studentId = feeStructure.student?.id || feeStructure.student_id;
    const batchId = feeStructure.batch?.id || feeStructure.batch_id;
    const enrollment = enrollments?.find(enr => 
      (enr.student_id === studentId || enr.student?.id === studentId) &&
      (enr.batch_id === batchId || enr.batch?.id === batchId)
    );
    
    // Calculate enrollment fee paid
    const enrollmentFeePaid = enrollment?.enrollment_fee_paid ? (enrollment.enrollment_fee_amount || 0) : 0;
    
    // Calculate regular payments (excluding enrollment fee payments)
    const regularPayments = feeStructure.payments?.reduce((sum, payment) => {
      // Skip enrollment fee payments as they're handled separately
      if (payment.remarks && payment.remarks.toLowerCase().includes('enrollment fee')) {
        return sum;
      }
      return sum + payment.amount;
    }, 0) || 0;
    
    // Total paid includes both enrollment fee and regular payments
    const totalPaid = enrollmentFeePaid + regularPayments;
    const outstanding = feeStructure.final_amount - totalPaid;
    
    if (outstanding <= 0) return { status: 'Fully Paid', color: 'bg-green-900/30 text-green-300 border border-green-700' };
    if (totalPaid > 0) return { status: 'Partial', color: 'bg-yellow-900/30 text-yellow-300 border border-yellow-700' };
    return { status: 'Pending', color: 'bg-red-900/30 text-red-300 border border-red-700' };
  };

  const getTotalPaid = () => {
    if (!feeStructure) return 0;
    
    // Get enrollment fee payment for this student-batch combination
    const studentId = feeStructure.student?.id || feeStructure.student_id;
    const batchId = feeStructure.batch?.id || feeStructure.batch_id;
    const enrollment = enrollments?.find(enr => 
      (enr.student_id === studentId || enr.student?.id === studentId) &&
      (enr.batch_id === batchId || enr.batch?.id === batchId)
    );
    
    // Calculate enrollment fee paid
    const enrollmentFeePaid = enrollment?.enrollment_fee_paid ? (enrollment.enrollment_fee_amount || 0) : 0;
    
    // Calculate regular payments (excluding enrollment fee payments)
    const regularPayments = feeStructure.payments?.reduce((sum, payment) => {
      // Skip enrollment fee payments as they're handled separately
      if (payment.remarks && payment.remarks.toLowerCase().includes('enrollment fee')) {
        return sum;
      }
      return sum + payment.amount;
    }, 0) || 0;
    
    // Total paid includes both enrollment fee and regular payments
    return enrollmentFeePaid + regularPayments;
  };



  const [generatingInvoiceId, setGeneratingInvoiceId] = useState(null);

  const generateInvoiceMutation = useMutation({
    mutationFn: (paymentId) => feeAPI.generateInvoice(paymentId, true),
    onSuccess: (response) => {
      const emailSent = response.data?.email_sent;
      if (emailSent) {
        alert('Invoice generated and sent successfully!');
      } else {
        alert('Invoice generated successfully, but email could not be sent. Please check if the student has a valid email address.');
      }
      setGeneratingInvoiceId(null);
    },
    onError: (error) => {
      alert('Error generating invoice: ' + (error.response?.data?.detail || error.message));
      setGeneratingInvoiceId(null);
    },
  });

  const handleGenerateInvoice = (paymentId) => {
    setGeneratingInvoiceId(paymentId);
    generateInvoiceMutation.mutate(paymentId);
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      cash: 'Cash',
      online: 'Online Transfer',
      cheque: 'Cheque',
      card: 'Card'
    };
    return methods[method] || method;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (!feeStructure) {
    return (
      <div className="text-center py-8">
                  <p className="text-gray-300">Fee structure not found</p>
      </div>
    );
  }

  const paymentStatus = calculatePaymentStatus();
  const totalPaid = getTotalPaid();
  const outstanding = feeStructure.final_amount - totalPaid;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/fees')}
          className="flex items-center text-gray-300 hover:text-gray-100 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Fees
        </button>
        <div className="flex justify-between items-start">
          <div>
                    <h1 className="text-3xl font-bold text-white">Fee Structure Details</h1>
        <p className="text-gray-300 mt-2">View fee structure and payment history</p>
          </div>
          <span className={`px-3 py-1 text-sm font-semibold rounded-full ${paymentStatus.color}`}>
            {paymentStatus.status}
          </span>
        </div>
      </div>

      {/* Fee Structure Summary */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6 mb-6">
        <h2 className="text-xl font-semibold text-white mb-4">Fee Structure Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center">
              <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-400">Student</p>
                <p className="font-medium text-white">{getStudentName(feeStructure.student_id)}</p>
              </div>
            </div>
            <div className="flex items-center">
              <CalendarIcon className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-400">Batch</p>
                <p className="font-medium text-white">{getBatchName(feeStructure.batch?.id || feeStructure.batch_id)}</p>
              </div>
            </div>
            <div className="flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-400">Payment Type</p>
                <p className="font-medium text-white">
                  {feeStructure.payment_type === 'one_time' ? 'One Time Payment' : `${feeStructure.installment_count} Installments`}
                </p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-400">Final Amount</p>
                              <p className="text-2xl font-bold text-yellow-500">{formatIndianCurrency(feeStructure.final_amount)}</p>
            </div>
          </div>
        </div>
        
        {/* Fee Breakdown */}
        <div className="mt-6 p-4 bg-gray-700 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-300 mb-3">Fee Breakdown</h3>
          {(() => {
            const selectedBatch = getSelectedBatch();
            const baseFee = Math.max(0, feeStructure.total_fee - (selectedBatch?.fee_amount || 0));
            const enrollmentFee = selectedBatch?.fee_amount || 0;
            console.log('Fee Breakdown Debug:', { 
              total_fee: feeStructure.total_fee, 
              selectedBatch, 
              enrollmentFee, 
              baseFee,
              discount: feeStructure.discount_amount 
            });
            return (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-300">Base Fee:</span>
                  <span className="font-medium text-white">{formatIndianCurrency(baseFee)}</span>
                </div>
                {enrollmentFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-300">Enrollment Fee:</span>
                    <span className="font-medium text-blue-500">{formatIndianCurrency(enrollmentFee)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-300">Discount:</span>
                                      <span className="font-medium text-green-500">-{formatIndianCurrency(feeStructure.discount_amount)}</span>
                </div>
                {feeStructure.payment_type === 'installments' && feeStructure.installments && feeStructure.installments.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-300 text-xs">Installment Rounding:</span>
                    <span className="font-medium text-blue-500 text-xs">
                      {formatIndianCurrency(feeStructure.installments[0].amount)} × {feeStructure.installment_count} installments
                    </span>
                  </div>
                )}
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between">
                                            <span className="text-white font-medium">Total Amount:</span>
                    <span className="font-bold text-yellow-500">{formatIndianCurrency(feeStructure.final_amount)}</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Installments Section - Only show if payment type is installments */}
      {feeStructure.payment_type === 'installments' && feeStructure.installments && (() => {
        // Get enrollment data for this fee structure
        const studentId = feeStructure.student?.id || feeStructure.student_id;
        const batchId = feeStructure.batch?.id || feeStructure.batch_id;
        const enrollment = enrollments?.find(enr => 
          (enr.student_id === studentId || enr.student?.id === studentId) &&
          (enr.batch_id === batchId || enr.batch?.id === batchId)
        );
        
        return (
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-700">
              <h2 className="text-xl font-semibold text-white">Installments</h2>
                          <p className="text-sm text-gray-300 mt-1">
                              {feeStructure.installment_count} installments of {formatIndianCurrency(feeStructure.installments?.[0]?.amount || Math.round((feeStructure.final_amount - (enrollment?.enrollment_fee_paid ? enrollment.enrollment_fee_amount : 0)) / feeStructure.installment_count))} each
              {feeStructure.installments?.[0]?.amount && (
                <span className="text-blue-600 ml-2">
                  (rounded from {formatIndianCurrency(((feeStructure.final_amount - (enrollment?.enrollment_fee_paid ? enrollment.enrollment_fee_amount : 0)) / feeStructure.installment_count).toFixed(2))})
                </span>
              )}
            </p>
            </div>
          <div className="p-6">
            {feeStructure.installments.length > 0 ? (
              <div className="space-y-4">
                {feeStructure.installments.map((installment) => {
                  const getStatusColor = (status) => {
                    switch (status) {
                      case 'paid': return 'bg-green-900/30 text-green-300 border border-green-700';
                      case 'partial': return 'bg-yellow-900/30 text-yellow-300 border border-yellow-700';
                      case 'overdue': return 'bg-red-900/30 text-red-300 border border-red-700';
                      default: return 'bg-gray-900/30 text-gray-300 border border-gray-700';
                    }
                  };
                  
                  const getStatusLabel = (status) => {
                    switch (status) {
                      case 'paid': return 'Paid';
                      case 'partial': return 'Partial';
                      case 'overdue': return 'Overdue';
                      default: return 'Pending';
                    }
                  };
                  
                  const isOverdue = isDateInPast(installment.due_date);
                  
                  return (
                    <div key={installment._id || installment.id} className="border border-gray-600 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-4">
                            <h3 className="text-lg font-medium text-white">
                              {installment.description || `Installment ${installment.installment_number}`}
                            </h3>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(installment.status)}`}>
                              {getStatusLabel(installment.status)}
                            </span>
                            {isOverdue && installment.status !== 'paid' && installment.status !== 'overdue' && (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-900/30 text-red-300 border border-red-700">
                                Overdue
                              </span>
                            )}
                          </div>
                          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-400">Amount:</span>
                              <span className="ml-2 font-medium text-white">{formatIndianCurrency(installment.amount)}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Paid:</span>
                              <span className="ml-2 font-medium text-green-500">{formatIndianCurrency(installment.paid_amount || 0)}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Outstanding:</span>
                              <span className="ml-2 font-medium text-red-500">{formatIndianCurrency(installment.outstanding_amount || installment.amount)}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Due Date:</span>
                              <span className={`ml-2 font-medium text-white ${isOverdue && installment.status !== 'paid' ? 'text-red-500' : ''}`}>
                                {formatDateToIST(installment.due_date)}
                              </span>
                            </div>
                          </div>
                          
                          {/* Show payments for this installment */}
                          {installment.payments && installment.payments.length > 0 && (
                            <div className="mt-3">
                              <p className="text-sm text-gray-300 mb-2">Payments for this installment:</p>
                              <div className="space-y-2">
                                {installment.payments.map((payment) => (
                                  <div key={payment._id || payment.id} className="bg-gray-700 rounded p-2 text-sm">
                                    <div className="flex justify-between items-center">
                                      <span className="text-white">{formatIndianCurrency(payment.amount)} - {payment.payment_method}</span>
                                      <span className="text-gray-400">
                                        {formatDateToIST(payment.payment_date)}
                                      </span>
                                    </div>
                                    {payment.remarks && (
                                      <p className="text-gray-400 text-xs mt-1">{payment.remarks}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Action buttons */}
                        {installment.status !== 'paid' && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => navigate(`/fees/${feeStructureId}/payments/new?installment=${installment._id || installment.id}`)}
                              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700"
                            >
                              <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                              Pay Installment
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
                                  <h3 className="mt-2 text-sm font-medium text-gray-300">No installments created</h3>
                <p className="mt-1 text-sm text-gray-400">
                  Installments will be created automatically when the fee structure is saved.
                </p>
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* Payment Summary */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6 mb-6">
        <h2 className="text-xl font-semibold text-white mb-4">Payment Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
              <p className="text-sm text-gray-400">Total Paid</p>
                              <p className="text-2xl font-bold text-green-500">{formatIndianCurrency(totalPaid)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-400">Outstanding</p>
                              <p className="text-2xl font-bold text-red-500">{formatIndianCurrency(outstanding)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-400">Progress</p>
                          <div className="mt-2">
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-yellow-500 h-2 rounded-full" 
                    style={{ width: `${Math.min(100, (totalPaid / feeStructure.final_amount) * 100)}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-300 mt-1">
                  {Math.round((totalPaid / feeStructure.final_amount) * 100)}% Complete
                </p>
              </div>
          </div>
        </div>
      </div>

      {/* Payment History */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">Payment History</h2>
            <button
              onClick={() => navigate(`/fees/${feeStructureId}/payments/new`)}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700"
            >
              <CurrencyDollarIcon className="h-4 w-4 mr-1" />
              Record Payment
            </button>
          </div>
        </div>
        <div className="p-6">
          {/* Show enrollment fee payment if it exists */}
          {(() => {
            const studentId = feeStructure.student?.id || feeStructure.student_id;
            const batchId = feeStructure.batch?.id || feeStructure.batch_id;
            const enrollment = enrollments?.find(enr => 
              (enr.student_id === studentId || enr.student?.id === studentId) &&
              (enr.batch_id === batchId || enr.batch?.id === batchId)
            );
            
            const hasEnrollmentPayment = enrollment?.enrollment_fee_paid && enrollment?.enrollment_fee_amount > 0;
            const hasRegularPayments = feeStructure.payments && feeStructure.payments.length > 0;
            
            if (!hasEnrollmentPayment && !hasRegularPayments) {
              return (
                <div className="text-center py-8">
                  <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-300">No payments recorded</h3>
                  <p className="mt-1 text-sm text-gray-500">No payments have been recorded for this fee structure yet.</p>
                  <div className="mt-6">
                    <button
                      onClick={() => navigate(`/fees/${feeStructureId}/payments/new`)}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700"
                    >
                      <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                      Record First Payment
                    </button>
                  </div>
                </div>
              );
            }
            
            return (
              <div className="space-y-4">
                {/* Enrollment Fee Payment */}
                {hasEnrollmentPayment && (
                  <div className="border border-blue-600 rounded-lg p-4 bg-blue-900/20">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4">
                          <h3 className="text-lg font-medium text-blue-300">
                            {formatIndianCurrency(enrollment.enrollment_fee_amount)}
                          </h3>
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-900/30 text-blue-300 border border-blue-600">
                            Enrollment Fee
                          </span>
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-900/30 text-green-300 border border-green-700">
                            completed
                          </span>
                        </div>
                        <p className="text-blue-300 mt-1">
                          Enrollment fee for {getBatchName(feeStructure.batch?.id || feeStructure.batch_id)} • {formatDateToIST(enrollment.enrollment_fee_payment_date)}
                        </p>
                        <p className="text-sm text-blue-400 mt-1">Collected during enrollment</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Regular Payments */}
                {hasRegularPayments && feeStructure.payments.map((payment) => (
                  <div key={payment._id || payment.id} className="border border-gray-600 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4">
                                                      <h3 className="text-lg font-medium text-white">
                            {formatIndianCurrency(payment.amount)}
                          </h3>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            payment.status === 'completed' ? 'bg-green-900/30 text-green-300 border border-green-700' : 'bg-yellow-900/30 text-yellow-300 border border-yellow-700'
                          }`}>
                            {payment.status}
                          </span>
                        </div>
                                                    <p className="text-gray-300 mt-1">
                          {getPaymentMethodLabel(payment.payment_method)} • {formatDateToIST(payment.payment_date)}
                        </p>
                        {payment.transaction_id && (
                          <p className="text-sm text-gray-500">Transaction ID: {payment.transaction_id}</p>
                        )}
                        {payment.cheque_number && (
                          <p className="text-sm text-gray-500">Cheque: {payment.cheque_number} ({payment.bank_name})</p>
                        )}
                        {payment.received_by && (
                          <p className="text-sm text-gray-500">Received by: {payment.received_by}</p>
                        )}
                        {payment.remarks && (
                          <p className="text-sm text-gray-500 mt-1">Remarks: {payment.remarks}</p>
                        )}
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => handleGenerateInvoice(payment._id || payment.id)}
                          disabled={generatingInvoiceId === (payment._id || payment.id)}
                          className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Generate and send invoice email"
                        >
                          <EnvelopeIcon className="h-4 w-4 mr-1" />
                          {generatingInvoiceId === (payment._id || payment.id) ? 'Sending...' : 'Invoice'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default FeeDetail; 